import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import './Toast.css';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastContextType {
  showToast: (toast: Omit<Toast, 'id'>) => void;
  hideToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const hideToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newToast: Toast = {
      id,
      duration: 5000, // 5 segundos por padrão
      ...toast,
    };

    setToasts(prev => [...prev, newToast]);

    // Auto-remove toast após a duração especificada
    if (newToast.duration && newToast.duration > 0) {
      setTimeout(() => {
        hideToast(id);
      }, newToast.duration);
    }
  }, [hideToast]);

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      <ToastContainer toasts={toasts} onHide={hideToast} />
    </ToastContext.Provider>
  );
};

interface ToastContainerProps {
  toasts: Toast[];
  onHide: (id: string) => void;
}

const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onHide }) => {
  return (
    <div className="fixed top-4 left-4 right-4 sm:left-auto sm:right-4 z-[80] space-y-2 max-w-sm sm:w-full pointer-events-none">
      {toasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto">
          <ToastItem toast={toast} onHide={onHide} />
        </div>
      ))}
    </div>
  );
};

interface ToastItemProps {
  toast: Toast;
  onHide: (id: string) => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onHide }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  const handleHide = React.useCallback(() => {
    setIsLeaving(true);
    setTimeout(() => {
      onHide(toast.id);
    }, 300); // Match animation duration
  }, [onHide, toast.id]);

  React.useEffect(() => {
    // Trigger entrance animation
    setTimeout(() => setIsVisible(true), 10);

    // Auto-hide after duration
    if (toast.duration && toast.duration > 0) {
      const timer = setTimeout(() => {
        handleHide();
      }, toast.duration);

      return () => clearTimeout(timer);
    }
  }, [toast.duration, handleHide]);

  const getToastStyles = () => {
    const baseStyles = "bg-white border-l-4 shadow-lg rounded-lg p-4 transform transition-all duration-300 ease-in-out";
    
    if (isLeaving) {
      return `${baseStyles} translate-x-full opacity-0`;
    }
    
    if (isVisible) {
      return `${baseStyles} translate-x-0 opacity-100`;
    }
    
    return `${baseStyles} translate-x-full opacity-0`;
  };

  const getIconAndColor = () => {
    switch (toast.type) {
      case 'success':
        return {
          icon: <CheckCircle className="w-5 h-5 text-green-500" />,
          borderColor: 'border-green-500',
          bgColor: 'bg-green-50'
        };
      case 'error':
        return {
          icon: <AlertCircle className="w-5 h-5 text-red-500" />,
          borderColor: 'border-red-500',
          bgColor: 'bg-red-50'
        };
      case 'warning':
        return {
          icon: <AlertTriangle className="w-5 h-5 text-yellow-500" />,
          borderColor: 'border-yellow-500',
          bgColor: 'bg-yellow-50'
        };
      case 'info':
        return {
          icon: <Info className="w-5 h-5 text-blue-500" />,
          borderColor: 'border-blue-500',
          bgColor: 'bg-blue-50'
        };
      default:
        return {
          icon: <Info className="w-5 h-5 text-gray-500" />,
          borderColor: 'border-gray-500',
          bgColor: 'bg-gray-50'
        };
    }
  };

  const { icon, borderColor, bgColor } = getIconAndColor();

  return (
    <div className={`${getToastStyles()} ${borderColor}`}>
      <div className="flex items-start">
        <div className={`flex-shrink-0 p-1 rounded-full ${bgColor} mr-3`}>
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-900">
            {toast.title}
          </div>
          {toast.message && (
            <div className="mt-1 text-sm text-gray-600">
              {toast.message}
            </div>
          )}
          {toast.action && (
            <div className="mt-3">
              <button
                onClick={toast.action.onClick}
                className="text-sm font-medium text-blue-600 hover:text-blue-500 transition-colors"
              >
                {toast.action.label}
              </button>
            </div>
          )}
        </div>
        <div className="flex-shrink-0 ml-3">
          <button
            onClick={handleHide}
            className="inline-flex text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Progress bar */}
      {toast.duration && toast.duration > 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200 rounded-b-lg overflow-hidden">
          <div 
            className="h-full bg-gray-400 transition-all ease-linear animate-shrink"
            style={{
              width: '100%',
              animationDuration: `${toast.duration}ms`
            }}
          />
        </div>
      )}
    </div>
  );
};

// Hook para facilitar o uso
export const useToastNotifications = () => {
  const { showToast } = useToast();

  const showSuccess = useCallback((title: string, message?: string, action?: Toast['action']) => {
    showToast({ type: 'success', title, message, action });
  }, [showToast]);

  const showError = useCallback((title: string, message?: string, action?: Toast['action']) => {
    showToast({ type: 'error', title, message, action, duration: 7000 }); // Erros ficam mais tempo
  }, [showToast]);

  const showWarning = useCallback((title: string, message?: string, action?: Toast['action']) => {
    showToast({ type: 'warning', title, message, action });
  }, [showToast]);

  const showInfo = useCallback((title: string, message?: string, action?: Toast['action']) => {
    showToast({ type: 'info', title, message, action });
  }, [showToast]);

  return {
    showSuccess,
    showError,
    showWarning,
    showInfo
  };
};
